import React, {ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState} from 'react';
import {Button, Input} from '@priority-software/priority-style-react';
import {texts} from '../../../../../../../texts';
import {keys} from '../../../../../../../../../constants/KeyCodes';
import {ModernTooltip} from '../../../../../../../../../components/Common/Tooltip/ModernTooltip';
import {ruleDialogID} from '../../../../../../../constants';
import {isValidEmail} from '@priority-software/priority-utils/lib/email';
import styles from './styles.module.scss';

type Props = {
    onSubmit: (value: string) => void;
};

export const EmailField = (props: Props) => {
    const {onSubmit} = props;
    const [value, setValue] = useState('');
    const inputRef = useRef<HTMLInputElement>();
    const [errorState, setErrorState] = useState(false);

    useEffect(() => {
        inputRef.current?.focus();
    }, [errorState]);

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setValue(e.currentTarget.value);
        setErrorState(false);
    }, []);

    const submitValue = () => {
        if (isValidEmail(value)) {
            onSubmit(value);
            setValue('');
            setErrorState(false);
        } else {
            setErrorState(true);
        }
    };

    const handleKeydown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.code === keys.ENTER) {
            e.preventDefault();
            e.stopPropagation();

            submitValue();
        }
    };

    const component = (
        <Input
            componentClasses={styles.component}
            inputRef={inputRef}
            onKeyDown={handleKeydown}
            value={value}
            placeholder={texts.email_field_placeholder}
            onChange={handleChange}
            state={errorState ? 'error' : undefined}
            inlinePadding="start"
        >
            {!!value && (
                <Button skin="primary" size="small" onClick={submitValue} roundedCorners={false}>
                    {texts.add_email_label}
                </Button>
            )}
        </Input>
    );

    return errorState ? (
        <ModernTooltip
            tooltip={texts.email_validation_error}
            portalId={ruleDialogID}
            position="top-start"
            arrowOffset={10}
        >
            {component}
        </ModernTooltip>
    ) : (
        component
    );
};
